// authSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { GET } from "@/services/api-request";

// Define a more specific type for the subscription data
interface SubscriptionPlan {
  isSubscribed: boolean;
}

// Define proper state interface
interface SubscriptionState {
  data: SubscriptionPlan | null;
  loading: boolean;
  error: string | null;
}

// Define response type
interface SubscriptionResponse {
  result: SubscriptionPlan;
}

// Define API error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

const initialState: SubscriptionState = {
  data: null,
  loading: false,
  error: null,
};

export const subscriptionFetch = createAsyncThunk<
  SubscriptionPlan,
  { token: string },
  { rejectValue: string }
>("subscription/fetchSubscription", async ({ token }, { rejectWithValue }) => {
  try {
    const { response } = await GET(`/subscription/status`, token);

    return response.data.result;
  } catch (err: unknown) {
    const error = err as ApiError;
    console.warn(error);
    return rejectWithValue(error.response?.data?.message || "Unknown error");
  }
});

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    resetSubscription: (state) => {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(subscriptionFetch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        subscriptionFetch.fulfilled,
        (state, action: PayloadAction<SubscriptionPlan>) => {
          state.data = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(subscriptionFetch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      });
  },
});

// Export any synchronous actions
export const { resetSubscription } = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
